import * as React from "react";
import { LoadingSpinner } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";

interface AgentActivity {
  userId: number;
  displayName: string;
  email: string;
  currentChats: number;
  status: string;
}

interface SiteActivity {
  siteId: number;
  companyName: string;
  activeAgents: AgentActivity[];
  totalAgents: number;
  todayConversations: {
    botOnly: number;
    humanOnly: number;
    botAndHuman: number;
    total: number;
  };
}

interface GlobalStats {
  totalAgentsOnline: number;
  totalSitesActive: number;
  globalConversationsToday: {
    botOnly: number;
    humanOnly: number;
    botAndHuman: number;
    total: number;
  };
}

export default function LiveMonitor() {
  const api = useApi();
  const [data, setData] = React.useState<{
    sites: SiteActivity[];
    global: GlobalStats;
  } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdate, setLastUpdate] = React.useState<Date>(new Date());
  const [refreshInterval, setRefreshInterval] = React.useState(10); // seconds

  const fetchData = React.useCallback(async () => {
    try {
      const response = await api.admin.get("LiveMonitor/Dashboard");
      const result = await response.json();
      setData(result);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch live data:", error);
      setLoading(false);
    }
  }, [api]);

  // Initial load
  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  React.useEffect(() => {
    const interval = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header with refresh controls */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Live Monitor</h1>
          <p className="text-slate-600 mt-1">
            Real-time view of all active sites and agents
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-slate-600 mb-2">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </div>
          <div className="flex gap-2 items-center">
            <label className="text-sm text-slate-600">Refresh every:</label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="px-3 py-1 border border-slate-300 rounded-md text-sm"
            >
              <option value={5}>5 seconds</option>
              <option value={10}>10 seconds</option>
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
            </select>
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Global Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-slate-600 mb-1">Sites Active</div>
              <div className="text-3xl font-bold text-blue-600">
                {data.global.totalSitesActive}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-slate-600 mb-1">Agents Online</div>
              <div className="text-3xl font-bold text-green-600">
                {data.global.totalAgentsOnline}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-slate-600 mb-1">Total Chats Today</div>
              <div className="text-3xl font-bold text-purple-600">
                {data.global.globalConversationsToday.total}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-sm text-slate-600 mb-1">Avg per Site</div>
              <div className="text-3xl font-bold text-orange-600">
                {data.global.totalSitesActive > 0
                  ? Math.round(data.global.globalConversationsToday.total / data.global.totalSitesActive)
                  : 0}
              </div>
            </div>
          </div>

          {/* Global Conversation Breakdown */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              Today's Conversations (All Sites)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-700">
                  {data.global.globalConversationsToday.botOnly}
                </div>
                <div className="text-sm text-blue-600 mt-1">🤖 Bot Only</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">
                  {data.global.globalConversationsToday.humanOnly}
                </div>
                <div className="text-sm text-green-600 mt-1">👤 Human Only</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-700">
                  {data.global.globalConversationsToday.botAndHuman}
                </div>
                <div className="text-sm text-purple-600 mt-1">🤖👤 Bot → Human</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-700">
                  {data.global.globalConversationsToday.total}
                </div>
                <div className="text-sm text-gray-600 mt-1">📊 Total</div>
              </div>
            </div>
          </div>

          {/* Per-Site Activity */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-slate-800">Site Activity</h2>

            {data.sites.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center text-slate-500">
                No active sites at the moment
              </div>
            ) : (
              data.sites.map((site) => (
                <div key={site.siteId} className="bg-white rounded-lg shadow-sm p-6">
                  {/* Site Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">
                        {site.companyName}
                      </h3>
                      <p className="text-sm text-slate-600">Site ID: {site.siteId}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        {site.totalAgents}
                      </div>
                      <div className="text-sm text-slate-600">Agents Online</div>
                    </div>
                  </div>

                  {/* Site Conversation Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">
                        {site.todayConversations.botOnly}
                      </div>
                      <div className="text-xs text-slate-600">Bot Only</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">
                        {site.todayConversations.humanOnly}
                      </div>
                      <div className="text-xs text-slate-600">Human Only</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-600">
                        {site.todayConversations.botAndHuman}
                      </div>
                      <div className="text-xs text-slate-600">Bot → Human</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-gray-700">
                        {site.todayConversations.total}
                      </div>
                      <div className="text-xs text-slate-600">Total Today</div>
                    </div>
                  </div>

                  {/* Agents List */}
                  {site.activeAgents.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">
                        Active Agents:
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {site.activeAgents.map((agent) => (
                          <div
                            key={agent.userId}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  agent.status === "Available"
                                    ? "bg-green-500"
                                    : agent.status === "Busy"
                                    ? "bg-red-500"
                                    : "bg-yellow-500"
                                }`}
                              />
                              <div>
                                <div className="text-sm font-medium text-slate-800">
                                  {agent.displayName}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {agent.email}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-blue-600">
                                {agent.currentChats}
                              </div>
                              <div className="text-xs text-slate-600">chats</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Auto-refresh indicator */}
      <div className="fixed bottom-4 right-4 bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-sm text-slate-600">Live</span>
      </div>
    </div>
  );
}
